import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDateTime } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import type { IcsAppointmentSuggestion } from '../useData';
import { emailPartitionAtSearch } from '../utils/emailPartitionAtQuery';

interface IcsSuggestionBannerProps {
  emailId: number;
  partitionAt?: string;
}

const IcsSuggestionBanner: React.FC<IcsSuggestionBannerProps> = ({ emailId, partitionAt }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation('emails');
  const [suggestions, setSuggestions] = useState<IcsAppointmentSuggestion[]>([]);
  const partitionQuery = emailPartitionAtSearch(partitionAt);

  useEffect(() => {
    api
      .get<IcsAppointmentSuggestion[]>(`/emails/${emailId}/ics-suggestions${partitionQuery}`)
      .then((res) => setSuggestions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSuggestions([]));
  }, [emailId, partitionAt, partitionQuery]);

  if (suggestions.length === 0) return null;

  const primary = suggestions[0];

  const createAppointment = () => {
    const params = new URLSearchParams();
    if (primary.start) params.set('clientAppScheduleDateTime', primary.start);
    if (primary.summary) params.set('clientAppName', primary.summary);
    navigate(`/appointments/new?${params.toString()}`);
  };

  return (
    <div
      className={cn(
        'border-x border-b px-4 sm:px-6 py-3',
        'bg-sky-50/90 dark:bg-sky-900/20 border-sky-200/70 dark:border-sky-800/50',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-sky-900 dark:text-sky-100">{t('icsDetected')}</p>
          <p className={cn('text-sm mt-1', colors.text)}>
            <strong>{primary.summary}</strong>
            {primary.start ? ` — ${formatDateTime(primary.start)}` : ''}
            {primary.location ? ` @ ${primary.location}` : ''}
          </p>
          {primary.attachmentFilename && (
            <p className={cn('text-xs mt-1', colors.textMuted)}>
              {t('icsFromAttachment', { filename: primary.attachmentFilename })}
            </p>
          )}
        </div>
        <Button variant="default" size="sm" onClick={createAppointment}>
          {t('icsCreateAppointment')}
        </Button>
      </div>
    </div>
  );
};

export default IcsSuggestionBanner;
